import React, { useRef, useCallback, useState, useEffect } from 'react';
import {
  Plus, X, Loader2, Check, Paintbrush, Download, RefreshCw,
  Paperclip, Minus, Clapperboard, Maximize2, Settings2,
} from 'lucide-react';
import { CupSodaIcon } from './CupSodaIcon';
import { useSlotScreenGeneratorStore } from '@store/slotScreenGeneratorStore';
import { DEFAULT_SCREEN_CONFIGS } from '../../constants/slot-screen-defaults';
import { IMAGE_MODELS } from '../../constants/image-generation';
import { generateScreenImages, saveScreenResultImage } from '../../services/slot-screen-generator';
import { useSlotScreenGenerator } from '../../hooks/useSlotScreenGenerator';
import type { SlotScreenId } from '@core/types/slot-screen-generator.types';

const IMAGE_COUNT_OPTIONS: Array<1 | 2 | 4 | 8> = [1, 2, 4, 8];

interface ModelDropdownProps {
  value: string;
  onChange: (value: string) => void;
}

function ModelDropdown({ value, onChange }: ModelDropdownProps) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const selectedLabel = IMAGE_MODELS.find(m => m.id === value)?.name ?? value;

  return (
    <div ref={ref} className="relative flex-1 min-w-0">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center px-2 py-1 bg-zinc-900 border border-zinc-700 rounded text-[11px] text-zinc-300 hover:border-zinc-500 transition-colors"
      >
        <span className="font-medium truncate">{selectedLabel}</span>
      </button>
      {open && (
        <div className="absolute bottom-full left-0 mb-1 z-[1000] bg-zinc-800 border border-zinc-600 rounded shadow-lg py-1 min-w-[200px] max-h-[200px] overflow-y-auto">
          {IMAGE_MODELS.map((model) => (
            <button
              key={model.id}
              onClick={() => { onChange(model.id); setOpen(false); }}
              className={`w-full text-left px-3 py-2 text-[11px] hover:bg-zinc-700 transition-colors
                ${model.id === value ? 'text-blue-400 bg-zinc-700/50' : 'text-zinc-300'}`}
            >
              {model.name}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function AutoAnimatedCupSoda() {
  const ref = useRef<import('./CupSodaIcon').CupSodaIconHandle>(null);
  useEffect(() => { ref.current?.startAnimation(); }, []);
  return <CupSodaIcon ref={ref} size={32} className="text-yellow-400" />;
}

function CardTooltip({ text, children }: { text: string; children: React.ReactNode }) {
  const [visible, setVisible] = useState(false);
  if (!text) return <>{children}</>;
  return (
    <div className="relative min-w-0" onMouseEnter={() => setVisible(true)} onMouseLeave={() => setVisible(false)}>
      {children}
      {visible && (
        <div className="absolute top-full left-0 mt-1.5 z-[1000] pointer-events-none">
          <div className="bg-zinc-800 border border-zinc-600 rounded px-2 py-1.5 text-[11px] text-zinc-300 leading-snug whitespace-normal w-[180px] shadow-lg">
            {text}
          </div>
        </div>
      )}
    </div>
  );
}

interface VideoSlotCardProps {
  screenId: SlotScreenId;
}

export function VideoSlotCard({ screenId }: VideoSlotCardProps) {
  const heroUploadRef = useRef<HTMLInputElement>(null);
  const refImageInputRef = useRef<HTMLInputElement>(null);
  const rerollAbortRef = useRef<AbortController | null>(null);
  const [isRerolling, setIsRerolling] = useState(false);

  const screen = useSlotScreenGeneratorStore(s => s.screens[screenId]);
  const slot = useSlotScreenGeneratorStore(s => s.videoSlotImages[screenId]);
  const globalModel = useSlotScreenGeneratorStore(s => s.generationSettings.model);
  const globalImageCount = useSlotScreenGeneratorStore(s => s.generationSettings.imageCount);
  const model = screen.generationModel ?? globalModel;
  const imageCount = screen.generationImageCount ?? globalImageCount;

  const setVideoSlotImage = useSlotScreenGeneratorStore(s => s.setVideoSlotImage);
  const clearVideoSlotImage = useSlotScreenGeneratorStore(s => s.clearVideoSlotImage);
  const addScreenResult = useSlotScreenGeneratorStore(s => s.addScreenResult);
  const setVideoSlotAnimationPrompt = useSlotScreenGeneratorStore(s => s.setVideoSlotAnimationPrompt);
  const selectScreenResult = useSlotScreenGeneratorStore(s => s.selectScreenResult);
  const openFullView = useSlotScreenGeneratorStore(s => s.openFullView);
  const setActiveScreen = useSlotScreenGeneratorStore(s => s.setActiveScreen);
  const updateScreenPrompt = useSlotScreenGeneratorStore(s => s.updateScreenPrompt);
  const addScreenReferenceImage = useSlotScreenGeneratorStore(s => s.addScreenReferenceImage);
  const removeScreenReferenceImage = useSlotScreenGeneratorStore(s => s.removeScreenReferenceImage);
  const updateScreenGenerationSettings = useSlotScreenGeneratorStore(s => s.updateScreenGenerationSettings);
  const syncVideoSlotsFromScreens = useSlotScreenGeneratorStore(s => s.syncVideoSlotsFromScreens);
  const deleteScreenResult = useSlotScreenGeneratorStore(s => s.deleteScreenResult);
  const openPromptDrawer = useSlotScreenGeneratorStore(s => s.openPromptDrawer);

  const { generate, cancel } = useSlotScreenGenerator();

  const config = DEFAULT_SCREEN_CONFIGS[screenId];

  // Resolve display image: uploaded slot image > selected screen result
  const heroUrl = slot?.imageUrl ?? null;
  const selectedResult = screen.selectedResultId
    ? screen.generatedResults.find(r => r.id === screen.selectedResultId && r.status === 'ready')
    : null;
  const displayUrl = heroUrl ?? selectedResult?.imageUrl ?? null;
  const displayResult = heroUrl ? null : selectedResult;

  // ALL ready results shown as tiles (including selected)
  const readyResults = screen.generatedResults.filter(r => r.status === 'ready');
  const selectedIndex = readyResults.findIndex(r => r.id === screen.selectedResultId);

  const isGenerating = screen.status === 'generating';
  const hasPrompt = !!screen.userPrompt.trim();

  // --- Hero image upload ---
  const handleHeroUpload = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const url = URL.createObjectURL(file);
    const resultId = `upload-${Date.now()}`;
    const result = {
      id: resultId,
      imageUrl: url,
      savedPath: '',
      status: 'ready' as const,
      timestamp: Date.now(),
      prompt: 'Uploaded',
      model: 'upload',
      width: 0,
      height: 0,
    };
    // Add as a result so it appears in the tile strip and gets auto-selected
    addScreenResult(screenId, result);
    syncVideoSlotsFromScreens();
    // Save to project folder (fire-and-forget)
    saveScreenResultImage(screenId, result)
      .then((savedPath) => {
        if (savedPath) {
          useSlotScreenGeneratorStore.getState().updateResultSavedPath(screenId, resultId, savedPath);
        }
      })
      .catch(() => {});
    e.target.value = '';
  }, [screenId, addScreenResult, syncVideoSlotsFromScreens]);

  const handleClearUpload = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    if (slot?.imageUrl?.startsWith('blob:') && slot.source === 'upload') {
      URL.revokeObjectURL(slot.imageUrl);
    }
    clearVideoSlotImage(screenId);
  }, [screenId, slot, clearVideoSlotImage]);

  const handleDeleteResult = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    const resultId = screen.selectedResultId;
    if (!resultId) return;
    deleteScreenResult(screenId, resultId);
    syncVideoSlotsFromScreens();
  }, [screenId, screen.selectedResultId, deleteScreenResult, syncVideoSlotsFromScreens]);

  // --- Hero overlay actions ---
  const handleOpenFullView = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    setActiveScreen(screenId);
    openFullView(screenId, Math.max(0, selectedIndex));
  }, [screenId, selectedIndex, setActiveScreen, openFullView]);

  const handleSave = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    if (!displayUrl) return;
    const a = document.createElement('a');
    a.href = displayUrl;
    a.download = `screen-${screenId}.png`;
    a.click();
  }, [screenId, displayUrl]);

  const handleReroll = useCallback(async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!displayResult || isRerolling) return;
    setIsRerolling(true);
    rerollAbortRef.current?.abort();
    const controller = new AbortController();
    rerollAbortRef.current = controller;

    const store = useSlotScreenGeneratorStore.getState();
    try {
      for await (const { result: newResult } of generateScreenImages(
        store.systemPromptJson,
        screen,
        { model: screen.generationModel ?? store.generationSettings.model, imageCount: 1 },
        controller.signal,
      )) {
        useSlotScreenGeneratorStore.getState().replaceScreenResult(screenId, displayResult.id, newResult);
      }
      useSlotScreenGeneratorStore.getState().addToast({
        id: Date.now().toString(), type: 'success', message: 'Image rerolled', timestamp: Date.now(),
      });
    } catch (err) {
      if (!(err instanceof Error && err.name === 'AbortError')) {
        const msg = err instanceof Error ? err.message : String(err);
        useSlotScreenGeneratorStore.getState().addToast({
          id: Date.now().toString(), type: 'error', message: `Reroll failed: ${msg}`, timestamp: Date.now(),
        });
      }
    } finally {
      setIsRerolling(false);
      syncVideoSlotsFromScreens();
    }
  }, [screenId, displayResult, isRerolling, screen, syncVideoSlotsFromScreens]);

  // --- Generation ---
  const handleGenerate = useCallback(async () => {
    setActiveScreen(screenId);
    await Promise.resolve();
    await generate();
    syncVideoSlotsFromScreens();
  }, [screenId, setActiveScreen, generate, syncVideoSlotsFromScreens]);

  const handleCancel = useCallback(() => {
    cancel();
  }, [cancel]);

  // --- Model / count ---
  const handleModelChange = useCallback((v: string) => {
    updateScreenGenerationSettings(screenId, { generationModel: v });
  }, [screenId, updateScreenGenerationSettings]);

  const handleCountChange = useCallback((dir: 'prev' | 'next') => {
    const idx = IMAGE_COUNT_OPTIONS.indexOf(imageCount as 1 | 2 | 4 | 8);
    if (dir === 'prev' && idx > 0) updateScreenGenerationSettings(screenId, { generationImageCount: IMAGE_COUNT_OPTIONS[idx - 1] });
    else if (dir === 'next' && idx < IMAGE_COUNT_OPTIONS.length - 1) updateScreenGenerationSettings(screenId, { generationImageCount: IMAGE_COUNT_OPTIONS[idx + 1] });
  }, [imageCount, screenId, updateScreenGenerationSettings]);

  // --- Reference images ---
  const addImageFiles = useCallback((files: FileList | File[]) => {
    for (const file of Array.from(files)) {
      if (file.type.startsWith('image/')) {
        addScreenReferenceImage(screenId, URL.createObjectURL(file));
      }
    }
  }, [screenId, addScreenReferenceImage]);

  const handleRefImageInput = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files?.length) {
      addImageFiles(e.target.files);
      e.target.value = '';
    }
  }, [addImageFiles]);

  const handleRemoveRefImage = useCallback((index: number) => {
    const url = screen.referenceImages[index];
    if (url?.startsWith('blob:')) { try { URL.revokeObjectURL(url); } catch { /* ignore */ } }
    removeScreenReferenceImage(screenId, index);
  }, [screenId, screen.referenceImages, removeScreenReferenceImage]);

  const handlePromptPaste = useCallback((e: React.ClipboardEvent) => {
    const items = e.clipboardData?.items;
    if (!items) return;
    const imageFiles: File[] = [];
    for (let i = 0; i < items.length; i++) {
      if (items[i].type.startsWith('image/')) {
        const file = items[i].getAsFile();
        if (file) imageFiles.push(file);
      }
    }
    if (imageFiles.length > 0) {
      e.preventDefault();
      addImageFiles(imageFiles);
    }
  }, [addImageFiles]);

  return (
    <div className="w-[250px] min-w-[250px] flex-shrink-0 flex flex-col rounded-xl border border-zinc-700 bg-zinc-800 overflow-hidden">

      {/* ── Header ── */}
      <div className="flex items-center gap-1.5 px-2 pt-1 border-zinc-700/50 flex-shrink-0">
        <Clapperboard size={13} className="text-zinc-200 flex-shrink-0" />
        <CardTooltip text={config.description}>
          <span className="text-xs font-medium text-zinc-200 whitespace-nowrap cursor-default">
            {config.name}
          </span>
        </CardTooltip>
      </div>

      {/* ── Hero Image ── */}
      <div className="relative w-full flex-shrink-0 group" style={{ paddingBottom: '133%' }}>
        <div className="absolute inset-0 px-2 pt-2 pb-1">
          {displayUrl ? (
            <>
              <img
                src={displayUrl}
                alt={config.name}
                className="w-full h-full object-cover cursor-pointer border border-[0.5px] border-white/40 rounded-lg"
                onClick={handleOpenFullView}
              />

              {/* Action buttons overlay (hover) */}
              <div className="absolute top-1.5 right-1.5 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                {displayResult && (
                  <>
                    <button
                      onClick={handleOpenFullView}
                      title="Edit Region"
                      className="p-1 bg-black/70 rounded hover:bg-zinc-700 transition-colors"
                    >
                      <Paintbrush size={11} className="text-zinc-200" />
                    </button>
                    <button
                      onClick={handleSave}
                      title="Save image"
                      className="p-1 bg-black/70 rounded hover:bg-zinc-700 transition-colors"
                    >
                      <Download size={11} className="text-zinc-200" />
                    </button>
                    <button
                      onClick={handleReroll}
                      disabled={isRerolling}
                      title="Reroll"
                      className="p-1 bg-black/70 rounded hover:bg-zinc-700 disabled:opacity-50 transition-colors"
                    >
                      {isRerolling
                        ? <Loader2 size={11} className="text-zinc-200 animate-spin" />
                        : <RefreshCw size={11} className="text-zinc-200" />}
                    </button>
                    <button
                      onClick={handleDeleteResult}
                      title="Remove image"
                      className="p-1 bg-black/70 rounded hover:bg-red-900/80 transition-colors"
                    >
                      <X size={11} className="text-zinc-300" />
                    </button>
                  </>
                )}
                {slot?.source === 'upload' && (
                  <button
                    onClick={handleClearUpload}
                    title="Remove uploaded image"
                    className="p-1 bg-black/70 rounded hover:bg-red-900/80 transition-colors"
                  >
                    <X size={11} className="text-zinc-300" />
                  </button>
                )}
              </div>

              {/* Metadata badges — bottom left */}
              {displayResult && (
                <div className="absolute bottom-1.5 left-1.5 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                  <span className="px-1.5 py-0.5 bg-black/70 rounded text-[9px] text-zinc-300 font-medium">
                    {displayResult.model}
                  </span>
                  <span className="px-1.5 py-0.5 bg-black/70 rounded text-[9px] text-zinc-400 tabular-nums">
                    {displayResult.width}×{displayResult.height}
                  </span>
                </div>
              )}
            </>
          ) : isGenerating ? (
            <div className="w-full h-full flex flex-col items-center justify-center gap-2">
              <AutoAnimatedCupSoda />
              <span className="text-xs text-yellow-500">Generating...</span>
            </div>
          ) : (
            <button
              onClick={() => heroUploadRef.current?.click()}
              className="w-full h-full flex flex-col items-center justify-center gap-2 bg-zinc-800 hover:bg-zinc-900 transition-colors group/upload"
            >
              <div className="w-10 h-10 rounded-full border border-zinc-600 group-hover/upload:border-zinc-400 flex items-center justify-center transition-colors">
                <Plus size={18} className="text-zinc-500 group-hover/upload:text-zinc-300 transition-colors" />
              </div>
              <span className="text-xs text-zinc-600 group-hover/upload:text-zinc-400 transition-colors">
                Upload or generate below
              </span>
            </button>
          )}
        </div>
      </div>

      {/* ── Result Tiles (ready results + placeholders while generating) ── */}
      {(readyResults.length > 0 || isGenerating) && (
        <div className="flex gap-1 px-2 pb-1 border-zinc-700/40 flex-shrink-0 flex-wrap">
          {readyResults.map(r => (
            <button
              key={r.id}
              onClick={() => { selectScreenResult(screenId, r.id); syncVideoSlotsFromScreens(); }}
              className={`relative w-8 h-10 overflow-hidden border rounded-sm flex-shrink-0 transition-colors
                ${r.id === screen.selectedResultId
                  ? 'border-green-500'
                  : 'border-zinc-700 hover:border-zinc-500'}`}
              title="Use this variant"
            >
              <img src={r.imageUrl} alt="variant" className="w-full h-full object-cover" />
              {r.id === screen.selectedResultId && (
                <div className="absolute bottom-0 right-0 w-3.5 h-3.5 bg-green-500 flex items-center justify-center rounded-tl">
                  <Check size={8} className="text-white" />
                </div>
              )}
            </button>
          ))}
          {/* Placeholder tiles for in-flight results */}
          {isGenerating && Array.from({ length: Math.max(0, imageCount - readyResults.length) }).map((_, i) => (
            <div key={`placeholder-${i}`} className="w-8 h-10 border border-zinc-700 border-dashed rounded-sm flex-shrink-0 bg-zinc-900/50 animate-pulse" />
          ))}
        </div>
      )}

      {/* ── Prompt + Reference Images + Generation ── */}
      <div className="flex flex-col gap-1 px-2 pb-2 flex-shrink-0">
        <div className="relative group/prompt">
          <textarea
            value={screen.userPrompt}
            onChange={e => updateScreenPrompt(screenId, e.target.value)}
            onPaste={handlePromptPaste}
            placeholder="Describe this screen..."
            rows={2}
            className="w-full bg-zinc-900 border border-zinc-700 rounded px-2 py-1.5 pr-6 text-xs leading-relaxed text-zinc-200 resize-none outline-none focus:ring-1 focus:ring-blue-500/50 placeholder:text-zinc-600"
          />
          <button
            onClick={() => openPromptDrawer(screenId, 'description')}
            title="Expand editor"
            className="absolute top-1 right-1 p-0.5 text-zinc-600 hover:text-zinc-300 opacity-0 group-hover/prompt:opacity-100 transition-opacity rounded hover:bg-zinc-700"
          >
            <Maximize2 size={10} />
          </button>
        </div>

        {/* Reference images */}
        {screen.referenceImages.length > 0 && (
          <div className="flex gap-1 flex-wrap">
            {screen.referenceImages.map((url, idx) => (
              <div key={idx} className="relative group/ref w-7 h-7 rounded overflow-hidden border border-zinc-700 flex-shrink-0">
                <img src={url} alt={`ref ${idx + 1}`} className="w-full h-full object-cover" />
                <button
                  onClick={() => handleRemoveRefImage(idx)}
                  className="absolute inset-0 bg-black/60 flex items-center justify-center opacity-0 group-hover/ref:opacity-100 transition-opacity"
                >
                  <X size={8} className="text-zinc-200" />
                </button>
              </div>
            ))}
          </div>
        )}

        {/* Add reference */}
        <button
          onClick={() => refImageInputRef.current?.click()}
          className="flex items-center gap-1 text-[11px] text-zinc-500 hover:text-zinc-300 transition-colors self-start"
        >
          <Paperclip size={10} />
          Add reference
        </button>

        {/* Model | Count | Generate row */}
        <div className="flex items-center gap-1.5">
          <ModelDropdown value={model} onChange={handleModelChange} />

          {/* Count stepper */}
          <div className="flex items-center gap-0.5 flex-shrink-0">
            <button
              onClick={() => handleCountChange('prev')}
              disabled={imageCount <= 1}
              className="p-0.5 text-zinc-400 hover:text-zinc-200 disabled:text-zinc-600 disabled:cursor-not-allowed rounded hover:bg-zinc-700 transition-colors"
            >
              <Minus size={10} />
            </button>
            <span className="text-[11px] text-zinc-300 tabular-nums w-4 text-center">{imageCount}</span>
            <button
              onClick={() => handleCountChange('next')}
              disabled={imageCount >= 8}
              className="p-0.5 text-zinc-400 hover:text-zinc-200 disabled:text-zinc-600 disabled:cursor-not-allowed rounded hover:bg-zinc-700 transition-colors"
            >
              <Plus size={10} />
            </button>
          </div>

          {/* Cancel / Generate */}
          {isGenerating ? (
            <button
              onClick={handleCancel}
              className="flex items-center gap-1 px-2 py-1 text-[11px] text-zinc-300 border border-zinc-600 rounded hover:bg-zinc-700 transition-colors flex-shrink-0"
            >
              <X size={9} />
              Cancel
            </button>
          ) : (
            <button
              onClick={handleGenerate}
              disabled={!hasPrompt}
              className="flex items-center gap-1 px-2.5 py-1 text-[11px] font-semibold border border-zinc-600 text-zinc-200 hover:bg-zinc-700 disabled:text-zinc-500 disabled:border-zinc-700 disabled:cursor-not-allowed rounded transition-colors flex-shrink-0"
            >
              Generate
            </button>
          )}
        </div>
      </div>

      {/* ── Animation Prompt ── */}
      <div className="flex flex-col gap-1 px-2 pt-1 pb-2 border-t border-zinc-700/50 flex-shrink-0">
        <label className="text-[10px] text-zinc-500 font-medium uppercase tracking-wide">Animation Prompt</label>
        <div className="relative group/anim">
          <textarea
            value={slot?.animationPrompt ?? ''}
            onChange={e => setVideoSlotAnimationPrompt(screenId, e.target.value)}
            placeholder="Describe the animation for the video... e.g. 'zoom in with golden sparkles'"
            rows={2}
            className="w-full bg-zinc-900 border border-zinc-700 rounded px-2 py-1.5 pr-6 text-xs leading-relaxed text-zinc-200 resize-none outline-none focus:ring-1 focus:ring-blue-500/50 placeholder:text-zinc-600"
          />
          <button
            onClick={() => openPromptDrawer(screenId, 'animation')}
            title="Expand editor"
            className="absolute top-1 right-1 p-0.5 text-zinc-600 hover:text-zinc-300 opacity-0 group-hover/anim:opacity-100 transition-opacity rounded hover:bg-zinc-700"
          >
            <Maximize2 size={10} />
          </button>
        </div>
      </div>

      {/* ── Animation System Prompt link ── */}
      <div className="flex items-center px-2 pb-1.5 flex-shrink-0">
        <button
          onClick={() => openPromptDrawer(screenId, 'animation-system')}
          className="flex items-center gap-1 text-[10px] text-zinc-500 hover:text-zinc-300 transition-colors"
          title="Edit animation system prompt for this screen"
        >
          <Settings2 size={9} />
          System Prompt
        </button>
      </div>

      {/* Hidden file inputs */}
      <input ref={heroUploadRef} type="file" accept="image/*" className="hidden" onChange={handleHeroUpload} />
      <input ref={refImageInputRef} type="file" accept="image/*" multiple className="hidden" onChange={handleRefImageInput} />
    </div>
  );
}
